<template>
  <main class="sf-page">

    <!-- Hero -->
    <div class="sf-page-hero">
      <div class="sf-page-hero__inner" data-aos="fade-up">
        <p class="sf-page-hero__eyebrow">
          <span class="sf-page-hero__eyebrow-dot"></span>
          Notre histoire &amp; nos valeurs
        </p>
        <h1 class="sf-h1">À propos de<br /><em>Stag Fondation</em></h1>
        <p class="sf-page-hero__sub">Association de la Société Civile à caractère social et culturel — Oyack source, Douala 3<sup>ème</sup>, Cameroun</p>
      </div>
    </div>

    <!-- Mission + Vision + Siège -->
    <section class="sf-section sf-section--cream">
      <div class="sf-container">
        <div class="ab-mv-grid">
          <div class="ab-mv-card" data-aos="fade-right" data-aos-duration="700">
            <div class="ab-mv-card__icon"><Target /></div>
            <h2 class="sf-h3">Notre Mission</h2>
            <p>Stag Fondation œuvre pour l'éducation des enfants défavorisés et l'inclusion numérique, tout en créant un réseau d'associations solidaires. Nous facilitons l'accès à Internet dans les quartiers défavorisés et formons les jeunes aux compétences de demain.</p>
          </div>
          <div class="ab-mv-card" data-aos="fade-up" data-aos-duration="700">
            <div class="ab-mv-card__icon"><Eye /></div>
            <h2 class="sf-h3">Notre Vision</h2>
            <p>Promouvoir l'éducation, l'inclusion sociale et l'autonomisation des jeunes à travers des projets de rencontres et d'échanges entre groupes issus de classes sociales différentes, pour bâtir une société camerounaise plus équitable et connectée.</p>
          </div>
          <div class="ab-mv-card" data-aos="fade-left" data-aos-duration="700">
            <div class="ab-mv-card__icon"><MapPin /></div>
            <h2 class="sf-h3">Statut &amp; Siège</h2>
            <p>Association de la Société Civile à caractère social et culturel, reconnue au Cameroun. Siège social établi à <strong>Oyack source, arrondissement de Douala 3<sup>ème</sup></strong>. Durée de vie illimitée, ouverte aux membres résidents ou non au Cameroun.</p>
          </div>
        </div>
      </div>
    </section>


    <!-- Objectifs statutaires -->
    <section class="sf-section sf-section--cream-d">
      <div class="sf-container">
        <div class="sf-section-hdr" data-aos="fade-up">
          <span class="sf-label">Article 2 des statuts</span>
          <h2 class="sf-h2">Nos <em>objectifs</em></h2>
        </div>
        <div class="ab-values-grid">
          <div
            v-for="(val, i) in values"
            :key="i"
            class="ab-value-card"
            data-aos="fade-up"
            data-aos-duration="600"
            :data-aos-delay="i * 90"
          >
            <div class="ab-value-card__num">{{ String(i+1).padStart(2,'0') }}</div>
            <div class="ab-value-card__icon"><component :is="val.icon" /></div>
            <h3 class="ab-value-card__title">{{ val.title }}</h3>
            <p class="ab-value-card__desc">{{ val.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Membres -->
    <section class="sf-section sf-section--cream">
      <div class="sf-container">
        <div class="sf-section-hdr" data-aos="fade-up">
          <span class="sf-label">Article 4 des statuts</span>
          <h2 class="sf-h2">Nos <em>membres</em></h2>
        </div>
        <div class="ab-membres-grid">
          <div class="ab-membre-card" data-aos="fade-up" data-aos-delay="0">
            <div class="ab-membre-card__icon"><Users /></div>
            <h3>Membres actifs</h3>
            <p>Toute personne physique qui s'acquitte de ses cotisations annuelles et participe activement aux activités de l'Association.</p>
          </div>
          <div class="ab-membre-card" data-aos="fade-up" data-aos-delay="120">
            <div class="ab-membre-card__icon"><Award /></div>
            <h3>Membres d'honneur</h3>
            <p>Toute personne physique rendant ou susceptible de rendre des services à Stag Fondation — admis par l'Assemblée Générale sur proposition du bureau exécutif.</p>
          </div>
          <div class="ab-membre-card" data-aos="fade-up" data-aos-delay="240">
            <div class="ab-membre-card__icon"><Heart /></div>
            <h3>Membres sympathisants</h3>
            <p>Toute personne physique admise comme telle par l'Assemblée Générale sur proposition du bureau exécutif. Ouverts aux résidents du Cameroun et de l'étranger.</p>
          </div>
        </div>
        <p class="ab-adhesion-note" data-aos="fade-up">
          Pour adhérer en qualité de membre actif, il faut être âgé d'au moins 18 ans, souscrire aux objectifs de Stag Fondation et respecter ses statuts et son règlement intérieur.
        </p>
      </div>
    </section>

    <!-- Histoire / Timeline -->
    <section class="sf-section sf-section--cream-d">
      <div class="sf-container">
        <div class="sf-section-hdr" data-aos="fade-up">
          <span class="sf-label">Notre parcours</span>
          <h2 class="sf-h2">Notre <em>histoire</em></h2>
        </div>
        <div class="ab-timeline">
          <div
            v-for="(evt, i) in timeline"
            :key="i"
            class="ab-tl-item"
            data-aos="fade-up"
            data-aos-duration="600"
            :data-aos-delay="i * 100"
          >
            <div class="ab-tl-item__year">{{ evt.year }}</div>
            <div class="ab-tl-item__dot"></div>
            <div class="ab-tl-item__content">
              <h3 class="ab-tl-item__title">{{ evt.title }}</h3>
              <p class="ab-tl-item__desc">{{ evt.desc }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Membres fondateurs -->
    <section class="sf-section sf-section--forest">
      <div class="sf-container">
        <div class="sf-section-hdr" data-aos="fade-up">
          <span class="sf-label sf-label--light">L'équipe fondatrice</span>
          <h2 class="sf-h2 sf-h2--light">Membres <em>fondateurs</em></h2>
        </div>
        <div class="ab-founders-grid">
          <div
            v-for="(m, i) in teamMembers"
            :key="i"
            class="ab-founder-card"
            data-aos="fade-up"
            :data-aos-delay="i * 120"
          >
            <div class="sf-avatar ab-founder-card__avatar" style="width:72px;height:72px;font-size:1.5rem">{{ m.initials }}</div>
            <h3 class="ab-founder-card__name">{{ m.name }}</h3>
            <p class="ab-founder-card__role">{{ m.role }}</p>
          </div>
        </div>
      </div>
    </section>

  </main>
</template>

<script>
import AOS from 'aos'
import 'aos/dist/aos.css'
import { Target, Eye, Heart, Users, Wifi, BookOpen, Award, MapPin } from 'lucide-vue-next'

export default {
  name: 'AboutPage',
  components: { Target, Eye, Heart, Users, Wifi, BookOpen, Award, MapPin },
  data() {
    return {
      // Les 5 objectifs statutaires (Article 2 des statuts de Stag Fondation)
      values: [
        {
          icon: 'Users',
          title: 'Rencontres & Échanges',
          description: 'Initier des projets de rencontres et d\'échanges entre groupes de jeunes issus de classes sociales différentes.'
        },
        {
          icon: 'Target',
          title: 'Plateforme d\'association',
          description: 'Créer une plateforme d\'association reliant les acteurs de la société civile, les communautés et les jeunes.'
        },
        {
          icon: 'Wifi',
          title: 'Accès à Internet',
          description: 'Faciliter l\'accès à Internet dans les quartiers défavorisés pour réduire la fracture numérique.'
        },
        {
          icon: 'BookOpen',
          title: 'Éducation & Autonomisation',
          description: 'Promouvoir l\'éducation, l\'inclusion sociale et l\'autonomisation des jeunes camerounais.'
        },
        {
          icon: 'Heart',
          title: 'Activités communautaires',
          description: 'Organiser des activités sociales, éducatives, sportives et communautaires pour renforcer les liens.'
        },
      ],
       
      timeline: [
        {
          year: '2024',
          title: 'Création de Stag Fondation',
          desc: 'Fondation de l\'association à Oyack source, arrondissement de Douala 3ème, avec pour vocation l\'inclusion numérique et l\'éducation des jeunes défavorisés.'
        },
           { year:'2025', title:'Lancement de la premiere edition petite surface du championat d\'association ',         desc:'A vue la participation de 8 associations de l\'arrondissement de douala 3 ieme' },

                       { year:'2026', title:'Lancement de Stag Connect',                       desc:'qui vise a faire un sondage entre les ecoles ,les menages et jeunes des quartiers defavorisés.' },

        
      ],
      teamMembers: [
        { name: 'Président', role: 'Bureau exécutif', initials: 'SF' },
        { name: 'Secrétaire', role: 'Bureau exécutif', initials: 'SF' },
        { name: 'Trésorier', role: 'Bureau exécutif', initials: 'SF' },
      ]
    }
  },
  mounted() {
    AOS.init({ once: true, offset: 70, easing: 'ease-out-cubic', duration: 650 })
  }
}
</script>

<style>
@import './sf-design.css';
</style>

<style scoped>
/* Section header */
.sf-section-hdr { margin-bottom:3rem; }

/* Mission / Vision / Siège — 3 colonnes */
.ab-mv-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(260px,1fr)); gap:1.5rem; }
.ab-mv-card {
  background:var(--white); border-radius:8px; padding:2.5rem 2rem;
  box-shadow:var(--shadow-sm);
}
.ab-mv-card__icon { width:52px; height:52px; border-radius:8px; background:rgba(27,58,45,.1); display:flex; align-items:center; justify-content:center; margin-bottom:1.25rem; }
.ab-mv-card__icon :deep(svg) { width:26px; height:26px; color:var(--terra); }
.ab-mv-card h2 { margin-bottom:.75rem; }
.ab-mv-card p { font-size:.9rem; color:var(--ink-60); line-height:1.65; }

/* Objectifs (values) */
.ab-values-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(240px,1fr)); gap:2px; }
.ab-value-card {
  background:var(--white); padding:2.25rem 1.75rem;
  position:relative; overflow:hidden;
  transition:background .3s ease;
}
.ab-value-card::after {
  content:''; position:absolute; inset:0;
  background:var(--forest); transform:scaleY(0); transform-origin:bottom;
  transition:transform .45s cubic-bezier(.25,.46,.45,.94); z-index:0;
}
.ab-value-card:hover::after { transform:scaleY(1); }
.ab-value-card:hover .ab-value-card__num,
.ab-value-card:hover .ab-value-card__title,
.ab-value-card:hover .ab-value-card__desc { color:var(--cream); }
.ab-value-card:hover .ab-value-card__icon :deep(svg) { color:var(--amber); }

.ab-value-card__num { font-family:var(--fd); font-size:3rem; font-weight:700; color:var(--cream-d); line-height:1; margin-bottom:.5rem; position:relative; z-index:1; transition:color .3s; }
.ab-value-card__icon { width:24px; height:24px; margin-bottom:.85rem; position:relative; z-index:1; }
.ab-value-card__icon :deep(svg) { width:100%; height:100%; color:var(--terra); transition:color .3s; }
.ab-value-card__title { font-family:var(--fd); font-size:1.25rem; font-weight:600; color:var(--ink); margin-bottom:.5rem; position:relative; z-index:1; transition:color .3s; }
.ab-value-card__desc { font-size:.84rem; color:var(--ink-60); line-height:1.6; position:relative; z-index:1; transition:color .3s; }

/* Membres */
.ab-membres-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(260px,1fr)); gap:1.5rem; margin-bottom:2rem; }
.ab-membre-card {
  background:var(--white); border-radius:8px; padding:2rem 1.75rem;
  box-shadow:var(--shadow-sm); border-top:3px solid var(--terra);
  transition:transform .3s ease, box-shadow .3s ease;
}
.ab-membre-card:hover { transform:translateY(-4px); box-shadow:var(--shadow-md); }
.ab-membre-card__icon { width:44px; height:44px; border-radius:8px; background:rgba(200,90,42,.1); display:flex; align-items:center; justify-content:center; margin-bottom:1rem; }
.ab-membre-card__icon :deep(svg) { width:22px; height:22px; color:var(--terra); }
.ab-membre-card h3 { font-family:var(--fd); font-size:1.2rem; font-weight:600; color:var(--ink); margin-bottom:.5rem; }
.ab-membre-card p { font-size:.86rem; color:var(--ink-60); line-height:1.65; }

.ab-adhesion-note {
  background:rgba(27,58,45,.06); border-left:3px solid var(--forest);
  border-radius:0 6px 6px 0; padding:1rem 1.5rem;
  font-size:.87rem; color:var(--ink-60); line-height:1.65;
  max-width:680px;
}

/* Timeline */
.ab-timeline { max-width:720px; display:flex; flex-direction:column; gap:0; }
.ab-tl-item { display:grid; grid-template-columns:80px 24px 1fr; gap:0 1.25rem; align-items:flex-start; padding-bottom:2.5rem; position:relative; }
.ab-tl-item:last-child { padding-bottom:0; }
.ab-tl-item::before {
  content:''; position:absolute; left:calc(80px + 12px + .1rem); top:12px; bottom:0;
  width:1px; background:var(--ink-20);
}
.ab-tl-item:last-child::before { display:none; }
.ab-tl-item__year { font-family:var(--fd); font-size:1.1rem; font-weight:700; color:var(--terra); padding-top:2px; text-align:right; }
.ab-tl-item__dot { width:14px; height:14px; border-radius:50%; background:var(--terra); border:3px solid var(--cream); box-shadow:0 0 0 2px var(--terra); margin-top:5px; flex-shrink:0; }
.ab-tl-item__content { }
.ab-tl-item__title { font-family:var(--fd); font-size:1.2rem; font-weight:600; color:var(--ink); margin-bottom:.4rem; }
.ab-tl-item__desc { font-size:.86rem; color:var(--ink-60); line-height:1.6; }

/* Founders */
.ab-founders-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(220px,1fr)); gap:1.5rem; }
.ab-founder-card {
  background:rgba(255,255,255,.06); border:1px solid rgba(255,255,255,.1);
  border-radius:8px; padding:2rem; text-align:center;
  transition:background .3s ease;
}
.ab-founder-card:hover { background:rgba(255,255,255,.1); }
.ab-founder-card__avatar { margin:0 auto 1rem; }
.ab-founder-card__name { font-family:var(--fd); font-size:1.2rem; font-weight:600; color:var(--cream); margin-bottom:.25rem; }
.ab-founder-card__role { font-size:.82rem; color:var(--sage); }

/* Responsive */
@media (max-width:640px) {
  .ab-timeline { gap:0; }
  .ab-tl-item { grid-template-columns:60px 20px 1fr; }
}
</style>